import { useCallback, useEffect, useRef, useState } from 'react';
import { organisationsAPI } from '../../organisations/api/organisationsAPI';
import { isForbidden, organisationErrorMessage } from '../../organisations/lib/errorMessage';

/**
 * The staff dashboard's scope picker and Students data.
 *
 * Loads GET /api/organisations/scopes once (and again on reloadScopes, after
 * a group changes), then GET /api/organisations/students for the selected
 * scope and period. With no scope in the URL the server's default_scope is
 * used, so the sidebar can highlight what the table shows.
 *
 * A 403 from /scopes means the viewer is neither staff of an organisation
 * nor a platform admin; the page shows that as its own state, not an error.
 * The Overview tab's data is useOverviewData's, loaded only on that tab.
 */
const useTeacherDashboardData = ({ scope, period }) => {
  const [scopes, setScopes] = useState(null);
  const [scopesError, setScopesError] = useState('');
  const [forbidden, setForbidden] = useState(false);

  // The last /students answer, keyed by the scope and period it is for.
  // Only the answer to the latest request is kept, so a slow response for
  // a scope the teacher has since left never overwrites the one on screen,
  // and until the current one arrives the list reads as loading (never
  // briefly as "no students").
  const [studentsResult, setStudentsResult] = useState({ key: null, payload: null, error: '' });
  const studentsRequest = useRef(0);

  const reloadScopes = useCallback(async () => {
    setScopesError('');
    try {
      setScopes(await organisationsAPI.getScopes());
      setForbidden(false);
    } catch (err) {
      if (isForbidden(err)) setForbidden(true);
      else setScopesError(organisationErrorMessage(err, 'Could not load your schools. Please try again.'));
    }
  }, []);

  useEffect(() => { reloadScopes(); }, [reloadScopes]);

  const activeScope = scope || scopes?.default_scope || null;
  const studentsKey = `${activeScope}|${period}`;

  const reloadStudents = useCallback(async () => {
    if (!activeScope) return;
    const token = ++studentsRequest.current;
    const key = `${activeScope}|${period}`;
    try {
      const payload = await organisationsAPI.getStudents({ scope: activeScope, period });
      if (token === studentsRequest.current) setStudentsResult({ key, payload, error: '' });
    } catch (err) {
      if (token !== studentsRequest.current) return;
      setStudentsResult({
        key, payload: null, error: organisationErrorMessage(err, 'Could not load students. Please try again.'),
      });
    }
  }, [activeScope, period]);

  useEffect(() => { reloadStudents(); }, [reloadStudents]);

  const current = studentsResult.key === studentsKey ? studentsResult : { payload: null, error: '' };

  return {
    scopes,
    scopesLoaded: scopes !== null || forbidden || Boolean(scopesError),
    scopesError,
    forbidden,
    activeScope,
    students: current.payload?.students || [],
    periodLabel: current.payload?.period_label || '',
    studentsLoading: Boolean(activeScope) && studentsResult.key !== studentsKey,
    studentsError: current.error,
    reloadScopes,
    reloadStudents,
  };
};

export default useTeacherDashboardData;
